import getCreated from "Areas/SharedFeatures/types/utils/getCreated";
import getId from "Areas/SharedFeatures/types/utils/getId";
import isDocumentContent from "Areas/SharedFeatures/types/utils/isDocumentContent";
import isDocumentMapDataGeometry from "Areas/SharedFeatures/types/utils/isDocumentMapDataGeometry";
import isDocumentMapDataPoint from "Areas/SharedFeatures/types/utils/isDocumentMapDataPoint";
import { GEOSPATIAL_LAT_LON_DELIMITER } from "Common/componentUtils/constants";
import { formatDateTime } from "Common/dateTimeUtils/format";
import { MAP_GEOMETRY_TYPE } from "Common/dictionaries/mapGeometryType";
import { ColorWidget, GeospatialWidget, TextAreaWidget, TextWidget } from "Components/optionForm";
import filter from "lodash/filter";
import forEach from "lodash/forEach";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import isFinite from "lodash/isFinite";
import join from "lodash/join";
import map from "lodash/map";
import replace from "lodash/replace";
import sortBy from "lodash/sortBy";
import split from "lodash/split";
import trim from "lodash/trim";
import uniq from "lodash/uniq";
import React, { memo, useCallback, useLayoutEffect, useMemo, useState } from "react";
import locale from "./properties.locale";
const calculateCoords = (singleData) => {
    if (!singleData)
        return undefined;
    if (isDocumentMapDataPoint(singleData))
        return join([
            singleData.Latitude,
            singleData.Longitude
        ], GEOSPATIAL_LAT_LON_DELIMITER);
    if (isDocumentMapDataGeometry(singleData))
        return join(map(singleData.Points, pair => join(pair, GEOSPATIAL_LAT_LON_DELIMITER)), "; ");
    return undefined;
};
function Properties(props) {
    var _a;
    const singleData = useMemo(() => { var _a; return (((_a = props.data) === null || _a === void 0 ? void 0 : _a.length) !== 1 ? undefined : props.data[0]); }, [props.data]);
    const [coords, setCoords] = useState(() => calculateCoords(singleData));
    const ids = useMemo(() => {
        const result = new Set();
        forEach(props.data, d => {
            const id = getId(d);
            id && result.add(id);
        });
        return result;
    }, [props.data]);
    const idsUniqString = useMemo(() => join(sortBy(Array.from(ids)), "_"), [ids]);
    const handlePropertyChange = useCallback((value, fieldName) => {
        ids.size && fieldName && props.onDataUpdate && props.onDataUpdate(ids, fieldName, value);
    }, [idsUniqString, props.onDataUpdate]);
    const handleCoordsChange = useCallback((newCoords) => {
        if (!ids.size || !props.onDataUpdate)
            return;
        setCoords(newCoords);
        const points = filter(map(split(newCoords, GEOSPATIAL_LAT_LON_DELIMITER), e => Number.parseFloat(replace(trim(e), /(\.|,)/, "."))), isFinite);
        points.length === 2 && props.onDataUpdate(ids, "Points", [[points[0], points[1]]]);
    }, [idsUniqString, props.onDataUpdate]);
    useLayoutEffect(() => {
        setCoords(calculateCoords(singleData));
    }, [idsUniqString]);
    const description = useMemo(() => {
        const descriptions = uniq(map(props.data, e => get(e, "Description", get(e, "NodeSource.Description"))));
        return {
            value: descriptions.length === 1 ? descriptions[0] || "" : "",
            placeholder: descriptions.length === 1 ? locale.DESCRIPTION : locale.DIFFERENT_VALUES
        };
    }, [props.data]);
    const color = useMemo(() => {
        const colors = uniq(map(props.data, e => get(e, "Color", get(e, "NodeSource.Color"))));
        const value = colors.length === 1 ? colors[0] || "" : "";
        return {
            value,
            placeholder: colors.length === 1 ? locale.COLOR : locale.DIFFERENT_VALUES,
            hasClearBtn: Boolean(value || colors.length > 1)
        };
    }, [props.data]);
    return (React.createElement("div", { className: props.className }, !isEmpty(props.data) && (React.createElement(React.Fragment, null,
        ((_a = props.data) === null || _a === void 0 ? void 0 : _a.length) === 1 && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "mb-0_75" },
                React.createElement("div", { className: "text-light-on-surface-variant font-bolder  font-small" }, locale.CREATED),
                React.createElement("div", null, formatDateTime(getCreated(props.data[0])) || locale.CREATED_DATE_UNKNOWN)),
            (isDocumentMapDataGeometry(props.data[0]) || isDocumentContent(props.data[0])) && (React.createElement("div", { className: "mb-0_75" },
                React.createElement(TextWidget, { value: get(props.data[0], "Name") || "", fieldName: "Name", onChange: handlePropertyChange, readOnly: !props.onDataUpdate, label: locale.NAME, labelMode: "floating" }))))),
        React.createElement("div", { className: "mb-0_75" },
            React.createElement(TextAreaWidget, { value: description.value, defaultSingleLine: true, autoResizeEnabled: true, maxHeight: 72, fieldName: "Description", onChange: handlePropertyChange, readOnly: !props.onDataUpdate, label: description.placeholder, labelMode: "floating", previewTitle: locale.DESCRIPTION, usePreviewButton: true })),
        singleData && coords && (React.createElement("div", { className: "mb-0_75" }, isDocumentMapDataGeometry(singleData) &&
            singleData.MapGeometryType ===
                MAP_GEOMETRY_TYPE.POINT ? (React.createElement(GeospatialWidget, { value: coords, onChange: handleCoordsChange, label: locale.COORDS, labelMode: "floating", zIndex: props.zIndex })) : (React.createElement(TextAreaWidget, { value: coords, label: locale.COORDS, labelMode: "floating", readOnly: true, defaultSingleLine: true, autoResizeEnabled: true, maxHeight: 72, usePreviewButton: true })))),
        React.createElement("div", { className: "mb-1" },
            React.createElement("div", { className: "text-light-on-surface-variant font-small mb-0_25" }, locale.COLOR),
            React.createElement(ColorWidget, { value: color.value, fieldName: "Color", onChange: handlePropertyChange, placeholder: color.placeholder, hasClearBtn: color.hasClearBtn, zIndex: props.zIndex, readOnly: !props.onDataUpdate }))))));
}
export default memo(Properties);
